// RealtimeClient — WebSocket transport for the Mini App live stream
// (docs/miniapp-m2-tz.md §3.9). Connects to GET wss://…/api/miniapp/stream
// with the JWT in the query string, auto-reconnects with capped exponential
// backoff, and heartbeats with ping/pong (closes + reconnects on a dead link).
//
// In dev, `?mock_realtime=1` skips the socket and exposes
// `window.__mock_emit(type, data)` so the UI can be driven from DevTools.
import { API_BASE, IS_DEV } from '../env'

export interface RealtimeEvent {
  type: string
  [k: string]: unknown
}

type Listener = (e: RealtimeEvent) => void

const PING_INTERVAL_MS = 30_000
const PONG_TIMEOUT_MS = 90_000
const MAX_BACKOFF_MS = 30_000

function streamUrl(): string {
  // API_BASE is '' for same-origin/dev-proxy, or an https origin in prod.
  const base = API_BASE || (typeof window !== 'undefined' ? window.location.origin : '')
  const wsBase = base.replace(/^http/i, 'ws')
  return `${wsBase}/api/miniapp/stream`
}

export class RealtimeClient {
  private ws: WebSocket | null = null
  private token = ''
  private attempt = 0
  private closedByUs = false
  private pingTimer: ReturnType<typeof setInterval> | null = null
  private lastPongAt = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private readonly listeners = new Set<Listener>()

  /** Subscribe to events. Returns an unsubscribe fn. */
  on(cb: Listener): () => void {
    this.listeners.add(cb)
    return () => this.listeners.delete(cb)
  }

  private emit(e: RealtimeEvent): void {
    this.listeners.forEach((l) => {
      try { l(e) } catch { /* listener errors never break the stream */ }
    })
  }

  /** Open (or re-open) the stream for a JWT. Idempotent-ish: a new token
   *  replaces the old connection. */
  connect(token: string): void {
    this.token = token
    this.closedByUs = false

    // Dev mock: no socket, just a manual emitter on window.
    if (IS_DEV && new URLSearchParams(window.location.search).get('mock_realtime') === '1') {
      ;(window as unknown as { __mock_emit?: (t: string, d?: object) => void }).__mock_emit =
        (type: string, data: object = {}) => this.emit({ type, ...data })
      // eslint-disable-next-line no-console
      console.info('[realtime] mock mode — call window.__mock_emit("task.created", {...})')
      return
    }
    this.open()
  }

  /** Close for good (no reconnect). Called on logout / unmount. */
  close(): void {
    this.closedByUs = true
    this.stopHeartbeat()
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null }
    try { this.ws?.close() } catch { /* noop */ }
    this.ws = null
  }

  private open(): void {
    if (!this.token) return
    let ws: WebSocket
    try {
      ws = new WebSocket(`${streamUrl()}?token=${encodeURIComponent(this.token)}`)
    } catch {
      this.scheduleReconnect()
      return
    }
    this.ws = ws

    ws.onopen = () => {
      this.attempt = 0
      this.lastPongAt = Date.now()
      this.startHeartbeat()
    }
    ws.onmessage = (ev) => {
      let e: RealtimeEvent
      try { e = JSON.parse(ev.data) } catch { return }
      if (e.type === 'pong') { this.lastPongAt = Date.now(); return }
      this.emit(e)
    }
    ws.onerror = () => { try { ws.close() } catch { /* noop */ } }
    ws.onclose = () => {
      this.stopHeartbeat()
      if (!this.closedByUs) this.scheduleReconnect()
    }
  }

  private scheduleReconnect(): void {
    if (this.closedByUs || this.reconnectTimer) return
    const delay = Math.min(1000 * 2 ** this.attempt, MAX_BACKOFF_MS)
    this.attempt += 1
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      if (!this.closedByUs) this.open()
    }, delay)
  }

  private startHeartbeat(): void {
    this.stopHeartbeat()
    this.pingTimer = setInterval(() => this.heartbeat(), PING_INTERVAL_MS)
  }

  private stopHeartbeat(): void {
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null }
  }

  private heartbeat(): void {
    // No pong within the window → assume a half-open socket, force a cycle.
    if (Date.now() - this.lastPongAt > PONG_TIMEOUT_MS) {
      try { this.ws?.close() } catch { /* onclose triggers reconnect */ }
      return
    }
    try { this.ws?.send(JSON.stringify({ type: 'ping' })) } catch { /* noop */ }
  }
}

// Process-wide singleton — one stream per Mini App session.
export const realtime = new RealtimeClient()
